import { Permission } from '../lib';
import { useCurrentCompany } from './use-current-company';
import { usePermission } from './use-permission';

/**
 * Role-aware hook for Bizy visibility.
 *
 * Admins see Bizy when `bizy_for_admins` is enabled; employees see it when
 * `bizy_for_employees` is enabled. The backend resolution already cascades
 * the parent `bizy` state into each child, so checking children is sufficient.
 *
 * Falls back to `bizy_enabled` only when `feature_states` is entirely absent
 * (e.g., legacy /me payloads). When `feature_states` is present, the role-specific
 * key is authoritative — a missing key resolves to `false` rather than leaking
 * through the legacy fallback.
 */
export function useIsBizyEnabled(): boolean {
	const company = useCurrentCompany();
	const isAdmin = usePermission(Permission.CanViewAdmin);

	const states = company?.feature_states;
	const key = isAdmin ? 'bizy_for_admins' : 'bizy_for_employees';

	if (states) {
		return states[key] ?? false;
	}

	return company?.bizy_enabled || false;
}
